import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayoutMain from "../../../../layout/dashboard/DashboardLayoutMain";
import { RazorButton } from "../../../../components/razor-reuse";
import TabLink from "../../../../components/fragment/tab-link/TabLink";
import ReuseModal from "../../../../components/fragment/modal/ReuseModal";
import RootState, { ThirdPartyVendorControl } from "../../../../redux/type";
import { systemRoles } from "../../../../utils/roles";
import {
  deleteVendor,
  getAllThirdPartyVendors,
  getAllVendors,
  updateVendor,
} from "../../../../redux/thirdParty";
import NoContentPage from "../../../../layout/dashboard/no-content/NoContentPage";
import CreateVendorModal from "./modal/CreateVendorModal";
import VendorCard from "./components/VendorCard";
import {
  buildUpdateVendorPayload,
  VENDOR_CATEGORY_TABS,
  vendorDisplayName,
  type VendorEditState,
} from "./vendorControlHelpers";

export default function ThirdPartyControlPanel() {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { vendor_control_panel, loading_vendors, updating_vendors } =
    useSelector((state: RootState) => state.thirdParty);

  const canView = !!user?.permissions?.includes(systemRoles[41]);
  const canAddVendor = !!user?.permissions?.includes(systemRoles[44]);

  const [activeCategory, setActiveCategory] = useState(
    VENDOR_CATEGORY_TABS[0].value,
  );
  const [editingVendorId, setEditingVendorId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ThirdPartyVendorControl | null>(
    null,
  );
  const [loadError, setLoadError] = useState(false);

  const fetchVendors = async () => {
    setLoadError(false);
    const result: any = await dispatch(getAllThirdPartyVendors() as any);
    if (!result?.payload?.data?.status) {
      setLoadError(true);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchVendors();
      dispatch(getAllVendors() as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleTabChange = (label: string) => {
    const tab = VENDOR_CATEGORY_TABS.find((t) => t.label === label);
    if (!tab) return;
    setActiveCategory(tab.value);
    setEditingVendorId(null);
  };

  const vendorsInTab = (vendor_control_panel || []).filter(
    (vendor) => vendor && vendor.category === activeCategory,
  );

  const handleSaveEdit = async (
    vendor: ThirdPartyVendorControl,
    editState: VendorEditState,
  ): Promise<boolean> => {
    const result: any = await dispatch(
      updateVendor({
        id: vendor.id,
        body: buildUpdateVendorPayload(editState),
      }) as any,
    );
    const success = !!result?.payload?.data?.status;
    if (success) {
      setEditingVendorId(null);
      fetchVendors();
    }
    return success;
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const result: any = await dispatch(deleteVendor(deleteTarget.id) as any);
    if (result?.payload?.data?.status) {
      setDeleteTarget(null);
      fetchVendors();
    }
  };

  return (
    <>
      <DashboardLayoutMain
        loading={loading_vendors && (vendor_control_panel?.length || 0) < 1}
        title="Third-Party Application"
      >
        <section className="px-[20px] py-[20px]">
          <div className="flex justify-between">
            <div className="space-y-[4px]">
              <p className="text-[16px] font-bold text-[#1e1e1e]">
                Third Party Vendor Control Panel
              </p>
              <p className="text-[14px] text-[#758089] font-medium">
                Manage third party vendor integrations for mobile and web
                services
              </p>
            </div>

            {canAddVendor && (
              <RazorButton
                color="black-light"
                label="Add New Vendor"
                className="btn"
                size="small"
                onClick={() => setCreateModalOpen(true)}
              />
            )}
          </div>

          <div className="mt-[24px] bg-white rounded-[8px] px-[8px]">
            <TabLink
              list={VENDOR_CATEGORY_TABS.map((tab) => ({
                label: tab.label,
                active: tab.value === activeCategory,
              }))}
              onClick={(param) => handleTabChange(param?.label)}
              className="single-customer-tab-link"
            />
          </div>

          <div className="mt-[24px] space-y-[16px]">
            {loadError ? (
              <div className="flex flex-col items-center justify-center rounded-[8px] border border-[#FDECEC] bg-[#FFF9F9] py-[48px]">
                <p className="text-[14px] font-medium text-[#DC2626]">
                  Couldn't load vendors
                </p>
                <p className="mt-[4px] text-[13px] text-[#758089]">
                  Something went wrong while fetching vendor controls.
                </p>
                <button
                  type="button"
                  onClick={fetchVendors}
                  className="mt-[12px] rounded-[8px] border border-[#734792] px-[14px] py-[8px] text-[13px] font-medium text-[#734792]"
                >
                  Retry
                </button>
              </div>
            ) : !loading_vendors && vendorsInTab.length === 0 ? (
              <NoContentPage
                title="No vendor available"
                text="There are no vendors configured for this category yet"
              />
            ) : (
              vendorsInTab.map((vendor) => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  isEditing={editingVendorId === vendor.id}
                  isSaving={updating_vendors}
                  onStartEdit={setEditingVendorId}
                  onCancelEdit={() => setEditingVendorId(null)}
                  onSaveEdit={handleSaveEdit}
                  onDelete={setDeleteTarget}
                />
              ))
            )}
          </div>
        </section>
      </DashboardLayoutMain>

      <CreateVendorModal
        onClose={() => setCreateModalOpen(false)}
        visible={createModalOpen}
      />

      <ReuseModal
        loading={updating_vendors}
        visible={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onBtnClick={handleConfirmDelete}
        deleteType
        btnColor=""
        btnLabel="Delete Vendor"
        title="Delete Vendor"
        deleteText={`Are you sure you want to deactivate "${
          deleteTarget ? vendorDisplayName(deleteTarget) : ""
        }"? Once confirmed, this would permanently delete this information from the database.`}
      />
    </>
  );
}
